import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Typography, Grid, Card, CardContent, CardMedia, Button, Stack, Chip,
  TextField, InputAdornment, Pagination, CircularProgress, List, ListItemButton, ListItemText,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { ezpPalette } from '../../components/EzpLayout';
import api from '../../services/api';

export default function EzpProductList() {
  const navigate = useNavigate();
  const { t } = useTranslation('ezp');
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [catId, setCatId] = useState(searchParams.get('cat_id') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const limit = 20;

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (catId) params.cat_id = catId;

      const res = await api.get('/ezp/products', { params });
      if (res.data.success) {
        setProducts(res.data.items || []);
        setTotal(res.data.total || 0);
      }
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, catId]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    api.get('/ezp/categories').then(res => {
      if (res.data.success) setCategories(res.data.categories || []);
    }).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchParams({ search, cat_id: catId, page: '1' });
  };

  const handleCategoryClick = (id) => {
    setCatId(id === catId ? '' : id);
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ fontWeight: 950, mb: 1 }}>
          {t('list.title', 'EZP 商品目錄')}
        </Typography>
        <Typography variant="body1" sx={{ color: ezpPalette.textMuted, mb: 3 }}>
          {t('list.subtitle', `共 ${total.toLocaleString()} 件商品可供上架`, { total: total.toLocaleString() })}
        </Typography>

        <Grid container spacing={3}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            {/* Search */}
            <Box component="form" onSubmit={handleSearch} sx={{ mb: 3 }}>
              <TextField
                fullWidth size="small" placeholder={t('list.search_placeholder', '搜尋商品...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
                sx={{ bgcolor: 'white', borderRadius: 1 }}
              />
            </Box>

            {/* Categories */}
            {categories.length > 0 && (
              <Box sx={{ bgcolor: 'white', borderRadius: 2, border: `1px solid ${ezpPalette.border}`, overflow: 'hidden' }}>
                <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 700 }}>
                  {t('list.categories', '商品分類')}
                </Typography>
                <List dense disablePadding sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <ListItemButton selected={!catId} onClick={() => handleCategoryClick('')}>
                    <ListItemText primary={t('list.all_categories', '全部分類')} />
                  </ListItemButton>
                  {categories.map(cat => (
                    <ListItemButton
                      key={cat.cat_id || cat.id}
                      selected={catId === String(cat.cat_id || cat.id)}
                      onClick={() => handleCategoryClick(String(cat.cat_id || cat.id))}
                    >
                      <ListItemText primary={cat.cat_name || cat.name} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
          </Grid>

          {/* Product grid */}
          <Grid item xs={12} md={9}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: ezpPalette.primary }} />
              </Box>
            ) : products.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" sx={{ color: ezpPalette.textMuted }}>
                  {t('list.no_products', '沒有找到商品')}
                </Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {products.map(product => (
                    <Grid item xs={6} sm={4} md={3} key={product.goods_id}>
                      <Card
                        elevation={0}
                        sx={{
                          height: '100%', borderRadius: 2, border: `1px solid ${ezpPalette.border}`, cursor: 'pointer',
                          transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
                        }}
                        onClick={() => navigate(`/products/${product.goods_id}`)}
                      >
                        <CardMedia
                          component="img"
                          height="160"
                          image={product.image_url || product.goods_img || '/images/product-placeholder.svg'}
                          alt={product.goods_name}
                          onError={(e) => { e.currentTarget.src = '/images/product-placeholder.svg'; }}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="body2" sx={{
                            fontWeight: 600, mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: 40,
                          }}>
                            {product.goods_name}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: ezpPalette.textMuted }}>
                              {t('list.cost', '進貨')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: ezpPalette.primary }}>
                              ${Math.round(parseFloat(product.shop_price) || 0)}
                            </Typography>
                          </Stack>
                          <Chip
                            label={`${t('list.profit', '利潤')} $${product.estimated_profit}`}
                            size="small"
                            sx={{ bgcolor: '#E8F5E9', color: ezpPalette.primary, fontWeight: 600, fontSize: '0.7rem' }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={totalPages} page={page}
                      onChange={(_, v) => { setPage(v); window.scrollTo(0, 0); }}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
